import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';

import { IOrderCreateManualQuery, useCreateManualOrderMutation } from '@entities/orders';
import { MerchantsSelectAsync } from '@features/merchants';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { FormItemWithError } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';
import { isEmptyObject } from '@shared/utils/isEmptyObject';

interface IProps {
  refetch: () => void;
}

type TField = keyof IOrderCreateManualQuery;
type TFieldErrors = Partial<Record<TField, string>>;

interface IFieldConfig {
  name: TField;
  label: string;
  placeholder?: string;
  inputMode?: 'decimal';
}

const FIELDS: IFieldConfig[] = [
  // Rendered as the merchant dropdown, which submits the merchant's short name
  { name: 'merchant_short_name', label: 'Merchant short name' },
  {
    name: 'merchant_customer_id',
    label: 'Merchant customer ID',
    placeholder: 'Customer ID at the merchant',
  },
  { name: 'utr', label: 'UTR', placeholder: 'Transaction UTR' },
  { name: 'amount', label: 'Amount', placeholder: '0.00', inputMode: 'decimal' },
];

const REQUIRED_FIELDS: TField[] = ['merchant_short_name', 'merchant_customer_id', 'utr', 'amount'];

// The backend accepts at most two decimals; the value is sent exactly as typed (no separators)
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

const REQUIRED_MESSAGES: Record<TField, string> = {
  merchant_short_name: 'Merchant short name is required',
  merchant_customer_id: 'Merchant customer ID is required',
  utr: 'UTR is required',
  amount: 'Amount is required',
};

const AMOUNT_FORMAT_MESSAGE = 'Amount must be a positive number with at most 2 decimals';

// FormItemWithError renders only API-shaped errors ({ message } / { errors }); a network-level
// failure (FETCH_ERROR, PARSING_ERROR, …) would otherwise leave the user without any feedback.
const GENERIC_ERROR_MESSAGE = 'Failed to create the order. Please try again.';

const EMPTY_FORM: IOrderCreateManualQuery = {
  merchant_short_name: '',
  merchant_customer_id: '',
  utr: '',
  amount: '',
};

interface IApiErrorData {
  message?: string;
  errors?: Record<string, string | string[]>;
}

const getApiErrorData = (
  error: FetchBaseQueryError | SerializedError | undefined,
): IApiErrorData | undefined =>
  error && 'data' in error ? (error.data as IApiErrorData | undefined) : undefined;

// Local validation errors are shaped like a backend 422 so FormItemWithError renders them
// under the matching field exactly like the real ones
const toValidationError = (errors: TFieldErrors): FetchBaseQueryError => ({
  status: 422,
  data: {
    errors: Object.fromEntries(Object.entries(errors).map(([field, message]) => [field, [message]])),
  },
});

const validate = (values: IOrderCreateManualQuery): TFieldErrors => {
  const errors: TFieldErrors = {};

  REQUIRED_FIELDS.forEach(field => {
    if (!values[field]) errors[field] = REQUIRED_MESSAGES[field];
  });

  if (values.amount && (!AMOUNT_PATTERN.test(values.amount) || Number(values.amount) <= 0)) {
    errors.amount = AMOUNT_FORMAT_MESSAGE;
  }

  return errors;
};

const trimValues = (values: IOrderCreateManualQuery): IOrderCreateManualQuery => ({
  merchant_short_name: values.merchant_short_name.trim(),
  merchant_customer_id: values.merchant_customer_id.trim(),
  utr: values.utr.trim(),
  amount: values.amount.trim(),
});

export const CreateManualOrder = ({ refetch }: IProps) => {
  const [values, setValues] = useState<IOrderCreateManualQuery>(EMPTY_FORM);
  const [localErrors, setLocalErrors] = useState<TFieldErrors>({});

  const [createManualOrder, { isLoading, isError, error }] = useCreateManualOrderMutation();
  const { closeModal } = useModal();

  // Synchronous guard: the disabled Create button only takes effect after the next render
  const isSubmitting = useRef(false);
  // The modal can be dismissed while the request is in flight; closeModal() pops the top-most
  // modal, so it must only run while this one is still mounted
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const localError = isEmptyObject(localErrors) ? null : toValidationError(localErrors);

  // Field-level backend errors go under their field; a message-only error (403, 5xx) or a
  // network failure is shown once below the form instead of under every field
  const apiErrorData = isError ? getApiErrorData(error) : undefined;
  const hasFieldErrors = FIELDS.some(({ name }) => Boolean(apiErrorData?.errors?.[name]));
  const generalErrorMessage =
    isError && !hasFieldErrors ? apiErrorData?.message || GENERIC_ERROR_MESSAGE : '';

  // Create stays disabled until every field has input; whitespace-only values are caught by
  // the trimmed validation on submit (same split as the FailOrder modal)
  const isIncomplete = Object.values(values).some(value => !value);

  const setFieldValue = (field: TField, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setLocalErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (field: TField) => (e: ChangeEvent<HTMLInputElement>) =>
    setFieldValue(field, e.target.value);

  const handleChangeMerchant = (shortName: string) =>
    setFieldValue('merchant_short_name', shortName);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Keep the inputs in sync with what is actually sent (e.g. whitespace-only becomes empty)
    const trimmed = trimValues(values);
    setValues(trimmed);

    const errors = validate(trimmed);
    setLocalErrors(errors);
    if (!isEmptyObject(errors)) return;

    if (isSubmitting.current) return;
    isSubmitting.current = true;

    const result = await createManualOrder(trimmed);
    isSubmitting.current = false;

    if ('data' in result) {
      if (isMounted.current) closeModal();
      refetch();
    }
  };

  return (
    <>
      <h2>Add balance</h2>

      <form className='popup-form' onSubmit={handleSubmit}>
        {FIELDS.map(({ name, label, placeholder, inputMode }) => (
          <FormItemWithError
            key={name}
            name={name}
            secondName='order'
            label={label}
            isError={Boolean(localError) || hasFieldErrors}
            error={localError || error}
          >
            {name === 'merchant_short_name' ? (
              <MerchantsSelectAsync valueKey='short_name' onChangeMerchant={handleChangeMerchant} />
            ) : (
              <input
                type='text'
                name={name}
                id={name}
                inputMode={inputMode}
                value={values[name]}
                onChange={handleChange(name)}
                placeholder={placeholder}
              />
            )}
          </FormItemWithError>
        ))}

        {generalErrorMessage && <div className='form-error'>{generalErrorMessage}</div>}

        <div className='form-submit'>
          <button type='submit' disabled={isIncomplete || isLoading}>
            Create
          </button>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};
